import { Box, Typography } from "@mui/material";
import LoadingButton from "../ui/LoadingButton";
import React from "react";
import { useIntl } from "../../contexts/IntlContext";

type InnerProps = {
  children: React.ReactNode;
  title: string;
  body: string;
  retryLabel: string;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundaryInner extends React.Component<InnerProps, State> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // TODO: enviar a um serviço de observabilidade (ex: Sentry) quando disponível.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h5" gutterBottom>
            {this.props.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480 }}>
            {this.props.body}
          </Typography>
          <LoadingButton variant="contained" onClick={this.handleRetry}>
            {this.props.retryLabel}
          </LoadingButton>
        </Box>
      );
    }

    return this.props.children;
  }
}

type Props = {
  children: React.ReactNode;
};

export function ErrorBoundary({ children }: Props) {
  const intl = useIntl();

  return (
    <ErrorBoundaryInner
      title={intl.formatMessage({ id: "errorBoundary.title" })}
      body={intl.formatMessage({ id: "errorBoundary.body" })}
      retryLabel={intl.formatMessage({ id: "errorBoundary.retry" })}
    >
      {children}
    </ErrorBoundaryInner>
  );
}
